import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

export const normalizeBoardSearch = value => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
const PAGE_SIZE = 20;

function BoardColumnCards({ items, label, getSearchText, renderCard, emptyMessage = "Sin tarjetas en esta etapa" }) {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const query = useDeferredValue(normalizeBoardSearch(search));
  const pending = query !== normalizeBoardSearch(search);
  // Normalize once per data change, rather than once per keystroke.
  const index = useMemo(() => items.map(item => ({ item, text: normalizeBoardSearch(getSearchText(item)) })), [items, getSearchText]);
  const matches = useMemo(() => {
    if (!query) return items;
    const words = query.split(/\s+/);
    return index.filter(entry => words.every(word => entry.text.includes(word))).map(entry => entry.item);
  }, [index, items, query]);
  // Urgent input updates reuse the mounted cards while React prepares results.
  const cards = useMemo(() => matches.slice(0, limit).map(renderCard), [matches, limit, renderCard]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = 0; }, [query]);
  const changeSearch = value => { setSearch(value); setLimit(PAGE_SIZE); };
  const clear = () => { changeSearch(""); inputRef.current?.focus(); };

  return <>
    <TextField
      inputRef={inputRef}
      value={search}
      onChange={event => changeSearch(event.target.value)}
      onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); clear(); } }}
      placeholder="Buscar…"
      size="small"
      fullWidth
      inputProps={{ "aria-label": `Buscar en ${label}`, autoComplete: "off" }}
      InputProps={{
        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17 }} /></InputAdornment>,
        endAdornment: search ? <InputAdornment position="end"><IconButton size="small" aria-label={`Limpiar búsqueda en ${label}`} onClick={clear} edge="end" sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment> : null,
      }}
      sx={{ flexShrink: 0, mb: 0.75, "& .MuiInputBase-root": { height: 32, fontSize: 12, bgcolor: "background.paper" }, "& input": { py: 0.5, minWidth: 0 } }}
    />
    {search && <Typography role="status" aria-live="polite" variant="caption" color="text.secondary" sx={{ flexShrink: 0, mb: 0.5, fontSize: 11 }}>{pending ? "Buscando…" : `${matches.length} de ${items.length}`}</Typography>}
    <Stack
      ref={listRef}
      data-board-cards
      spacing={1}
      role="region"
      aria-label={`Tarjetas de ${label}`}
      aria-busy={pending}
      tabIndex={0}
      sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehaviorY: "contain", scrollbarGutter: "stable", p: 0.5, "& > *": { flexShrink: 0 } }}
    >
      {cards}
      {matches.length > limit && <Button size="small" onClick={() => setLimit(value => value + PAGE_SIZE)}>Mostrar más ({matches.length - limit} restantes)</Button>}
      {!matches.length && <Box sx={{ py: 3, textAlign: "center" }}><Typography variant="caption" color="text.secondary">{query ? "Sin coincidencias en esta columna" : emptyMessage}</Typography>{query && <Button size="small" onClick={clear}>Limpiar búsqueda</Button>}</Box>}
    </Stack>
  </>;
}

export default memo(BoardColumnCards);
